/**
 * INS-9 option 3 — a failed `System.import` of a tenant module used to leave
 * the signed-in shell as empty chrome. Module bundles are cross-origin, and
 * the service worker does not cache them (DEV-1021 stays dormant until
 * DEV-1022). This module does not change that policy. It only makes the
 * failure visible in the content area, and retries when the link returns.
 */
import { computed, ref } from "vue";
import {
  LOAD_ERROR,
  addErrorHandler,
  getAppStatus,
  removeErrorHandler,
  triggerAppChange,
} from "single-spa";
import type { LifeCycles } from "single-spa";

type SpaAppError = Error & { appOrParcelName: string };

/**
 * single-spa will not retry an app in `LOAD_ERROR` until 200ms after the
 * failure (`getAppChanges` in single-spa). Waiting past that gate is what
 * makes `triggerAppChange` actually call `System.import` again.
 */
export const MODULE_LOAD_RETRY_DELAY_MS = 250;

export type ModuleLoadKind = "offline" | "generic";

interface RecordedFailure {
  kind: ModuleLoadKind;
  /** Overlays (comms, help-center) do not own the content area. */
  overlay: boolean;
}

/**
 * Fetch/network failures as browsers and SystemJS actually phrase them.
 * An HTTP status (`404 Not Found …`) is a response, not a network failure.
 */
const NETWORK_FAILURE =
  /failed to fetch|networkerror|network request failed|network error|load failed|error loading dynamically imported module|importing a module script failed|net::err_|internet connection appears to be offline|err_internet_disconnected|err_connection|err_name_not_resolved|fetch failed/i;

const failures = ref<Map<string, RecordedFailure>>(new Map());
const overlayNames = new Set<string>();

let guardsInstalled = false;
let retryTimer: number | null = null;

/** What the content area should show. Overlay-only failures stay quiet. */
export const moduleLoadKind = computed<ModuleLoadKind | null>(() => {
  let generic = false;
  for (const failure of failures.value.values()) {
    if (failure.overlay) continue;
    if (failure.kind === "offline") return "offline";
    generic = true;
  }
  return generic ? "generic" : null;
});

export function classifyModuleImportFailure(error: unknown, online: boolean): ModuleLoadKind {
  if (!online) return "offline";
  const message = error instanceof Error ? `${error.name} ${error.message}` : String(error ?? "");
  return NETWORK_FAILURE.test(message) ? "offline" : "generic";
}

export function rememberModuleRole(importKey: string, overlay: boolean): void {
  if (overlay) overlayNames.add(importKey);
  else overlayNames.delete(importKey);
}

function noteModuleLoadFailure(importKey: string, error: unknown, overlay: boolean): void {
  const next = new Map(failures.value);
  next.set(importKey, {
    kind: classifyModuleImportFailure(error, navigator.onLine),
    overlay,
  });
  failures.value = next;
}

function clearModuleLoadFailure(importKey: string): void {
  if (!failures.value.has(importKey)) return;
  const next = new Map(failures.value);
  next.delete(importKey);
  failures.value = next;
}

/**
 * SystemJS keeps a rejected load in its registry, so a later `System.import`
 * of the same specifier fails again without touching the network. Drop both
 * the specifier and the resolved URL.
 */
function dropSystemModule(importKey: string): void {
  const system = window.System;
  if (!system?.delete) return;
  const ids = new Set<string>([importKey]);
  try {
    if (system.resolve) ids.add(system.resolve(importKey));
  } catch {
    // No import-map entry — the specifier is the only id worth deleting.
  }
  for (const id of ids) {
    try {
      system.delete(id);
    } catch {
      // The module never made it into the registry.
    }
  }
}

/**
 * Ask single-spa to load the failed apps again. The screen stays up until an
 * import actually resolves — a retry that fails again leaves the same message.
 */
export function retryFailedModuleLoads(): void {
  for (const importKey of failures.value.keys()) dropSystemModule(importKey);
  if (retryTimer != null) window.clearTimeout(retryTimer);
  retryTimer = window.setTimeout(() => {
    retryTimer = null;
    // A rejected reroute is the same failure `onSpaError` already recorded.
    void Promise.resolve(triggerAppChange()).catch(() => undefined);
  }, MODULE_LOAD_RETRY_DELAY_MS);
}

function onOnline(): void {
  const waitingOnConnection = [...failures.value.values()].some((failure) => failure.kind === "offline");
  if (!waitingOnConnection) return;
  retryFailedModuleLoads();
}

function onSpaError(error: SpaAppError): void {
  const name = error.appOrParcelName;
  if (name && getAppStatus(name) === LOAD_ERROR) {
    noteModuleLoadFailure(name, error, overlayNames.has(name));
    return;
  }
  // single-spa only rethrows when NO handler is registered. Keep that for
  // lifecycle failures that are not a source-load error.
  window.setTimeout(() => {
    throw error;
  });
}

/** Idempotent: `registerTenantModules` may run once per boot, tests more often. */
export function installModuleLoadGuards(): void {
  if (guardsInstalled || typeof window === "undefined") return;
  guardsInstalled = true;
  addErrorHandler(onSpaError);
  window.addEventListener("online", onOnline);
}

/**
 * The single-spa loading function for one tenant module. A successful import
 * clears that module's failure; a rejection is recorded and rethrown so
 * single-spa still moves the app to `LOAD_ERROR`.
 */
export function loadTenantModule(importKey: string, overlay = false): Promise<LifeCycles> {
  return window.System.import<LifeCycles>(importKey).then(
    (mod) => {
      clearModuleLoadFailure(importKey);
      return mod;
    },
    (error: unknown) => {
      noteModuleLoadFailure(importKey, error, overlay);
      throw error;
    },
  );
}

/** Test isolation. Also detaches the window listener and the spa handler. */
export function resetModuleLoadState(): void {
  failures.value = new Map();
  overlayNames.clear();
  if (retryTimer != null) {
    window.clearTimeout(retryTimer);
    retryTimer = null;
  }
  if (guardsInstalled) {
    removeErrorHandler(onSpaError);
    window.removeEventListener("online", onOnline);
    guardsInstalled = false;
  }
}
